import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Images, X, FolderOpen, ChevronDown, GripVertical, Pencil } from "lucide-react";
import { api, mediaUrl, formatApiErrorDetail } from "@/lib/api";

const CATEGORIES = ["Siding", "Exterior Trim", "Soffit", "Windows & Doors", "Weather Protection & Insulation", "Roofing", "Extra Details"];
const CATEGORY_UNITS = { "Siding": "SQ", "Exterior Trim": "LNFT", "Soffit": "SQFT", "Windows & Doors": "EACH", "Weather Protection & Insulation": "SQFT", "Roofing": "SQ", "Extra Details": "LNFT" };
const UNIT_OPTIONS = [["SQ", "Per square (SQ — 100 sq ft)"], ["SQFT", "Per square foot (SQFT)"], ["LNFT", "Per linear foot (LNFT)"], ["EACH", "Per unit (EACH)"], ["FLAT", "One price (flat)"]];
const unitLabel = (u) => (u === "FLAT" ? "one price" : u);

const inputCls = "rounded-lg w-full bg-white/5 border border-white/15 px-4 py-3.5 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-primary transition-colors duration-300";
const selectCls = `${inputCls} appearance-none`;

const genId = () => (crypto.randomUUID ? crypto.randomUUID() : `sub-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);

const blankSub = () => ({ id: genId(), name: "", labor_rate: "", material_rate: "", description: "", photo: "" });

const SitePhotoPicker = ({ photos, onPick, idPrefix }) => (
  <div className="border border-white/10 p-2 max-h-56 overflow-y-auto" data-testid={`${idPrefix}-site-picker`}>
    {photos.length === 0 && <p className="text-white/40 text-xs py-6 text-center">No project photos on the website yet — upload some in the Projects tab first, or use "From phone".</p>}
    <div className="grid grid-cols-4 gap-2">
      {photos.map((ph) => (
        <button key={ph.storage_path} onClick={() => onPick(ph.storage_path)} data-testid={`${idPrefix}-site-photo-${ph.storage_path.split("/").pop()}`} className="relative group aspect-square overflow-hidden border border-transparent hover:border-primary transition-colors duration-200" title={ph.project}>
          <img src={mediaUrl(ph.storage_path)} alt={ph.project || "Site photo"} className="w-full h-full object-cover" loading="lazy" />
        </button>
      ))}
    </div>
  </div>
);

const COST_METHOD_LABELS = { mile: "Per mile", day: "Per day", hour: "Per hour worked", worker_day: "Per worker per day", flat: "Flat amount" };
const COST_METHOD_HINTS = {
  mile: "Total = miles per day × days × rate",
  day: "Total = days on site × rate",
  hour: "Total = workers × days × hours per day × rate",
  worker_day: "Total = workers × days × rate",
  flat: "Charged once per project",
};

const CostRow = ({ item, onSave, onDelete }) => {
  const [name, setName] = useState(item.name);
  const [rate, setRate] = useState(String(item.cost_rate ?? ""));
  return (
    <div className="border border-white/10 rounded-lg bg-card p-3 space-y-2" data-testid={`cost-item-${item.id}`}>
      <div className="flex items-center gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} onBlur={() => { const v = name.trim(); if (v && v !== item.name) onSave(item.id, { name: v }); else if (!v) setName(item.name); }} aria-label="Cost name" data-testid={`cost-name-${item.id}`} className={inputCls} />
        <button onClick={() => onDelete(item)} aria-label="Remove cost" data-testid={`cost-delete-${item.id}`} className="text-red-400/70 hover:text-red-300 p-2 shrink-0"><Trash2 size={15} /></button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <select value={item.calc_method || "flat"} onChange={(e) => onSave(item.id, { calc_method: e.target.value })} aria-label="Calculation method" data-testid={`cost-method-${item.id}`} className={selectCls}>
          {Object.entries(COST_METHOD_LABELS).map(([k, l]) => <option key={k} value={k} className="bg-[#252720]">{l}</option>)}
        </select>
        <input value={rate} onChange={(e) => setRate(e.target.value)} onBlur={() => { const v = parseFloat(rate) || 0; if (v !== (item.cost_rate || 0)) onSave(item.id, { cost_rate: v }); }} type="number" min="0" step="0.01" placeholder="$ rate" aria-label="Cost rate" data-testid={`cost-rate-${item.id}`} className={inputCls} />
      </div>
      <p className="text-[10px] text-white/35">{COST_METHOD_HINTS[item.calc_method || "flat"]}</p>
    </div>
  );
};

export const LibraryTab = () => {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState({ name: "", category: "Siding", unit: "" });
  const [creating, setCreating] = useState(false);
  const [openCats, setOpenCats] = useState(new Set());
  const [openItemId, setOpenItemId] = useState(null);
  const [draft, setDraft] = useState(null); // editable copy of the open item
  const [openSubId, setOpenSubId] = useState(null);
  const [saveState, setSaveState] = useState(null); // null | "saving" | "saved"
  const [busySubId, setBusySubId] = useState(null);
  const [sitePickerSubId, setSitePickerSubId] = useState(null);
  const [sitePhotos, setSitePhotos] = useState([]);
  const [showCostNew, setShowCostNew] = useState(false);
  const [costForm, setCostForm] = useState({ name: "", calc_method: "day", cost_rate: "" });
  const [customCats, setCustomCats] = useState([]);
  const [showNewCat, setShowNewCat] = useState(false);
  const [catForm, setCatForm] = useState({ name: "", unit: "SQFT" });
  const [catBusy, setCatBusy] = useState(false);

  const allCats = [...CATEGORIES, ...customCats.map((c) => c.name)];
  const customUnits = Object.fromEntries(customCats.map((c) => [c.name, c.unit]));
  const unitFor = (c) => CATEGORY_UNITS[c] || customUnits[c] || "EACH";
  const [catOrder, setCatOrder] = useState([]);
  const [dragCat, setDragCat] = useState(null);
  const orderedCats = catOrder.length
    ? [...allCats].sort((a, b) => {
        const ia = catOrder.indexOf(a); const ib = catOrder.indexOf(b);
        return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
      })
    : allCats;

  const dropOn = (target) => {
    if (!dragCat || dragCat === target) { setDragCat(null); return; }
    const next = orderedCats.filter((c) => c !== dragCat);
    next.splice(next.indexOf(target), 0, dragCat);
    setCatOrder(next);
    setDragCat(null);
    api.put("/admin/library/categories/order", { order: next })
      .then(() => toast.success("Category order saved — your library and proposal picker follow it"))
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)));
  };

  const createCategory = async () => {
    if (catForm.name.trim().length < 2) return toast.error("Category name is required");
    setCatBusy(true);
    try {
      const { data } = await api.post("/admin/library/categories", { name: catForm.name.trim(), unit: catForm.unit });
      setCustomCats((cs) => [...cs, data]);
      setCatForm({ name: "", unit: "SQFT" });
      setShowNewCat(false);
      setOpenCats((prev) => new Set(prev).add(data.name));
      toast.success(`"${data.name}" category created — now add items under it with New Item`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCatBusy(false);
    }
  };

  const [editCat, setEditCat] = useState(null); // {orig, name, unit}
  const [catEditBusy, setCatEditBusy] = useState(false);

  const saveCatEdit = async () => {
    if (!editCat || editCat.name.trim().length < 2) return toast.error("Category name is required");
    setCatEditBusy(true);
    try {
      const { data } = await api.put(`/admin/library/categories/${encodeURIComponent(editCat.orig)}`, { name: editCat.name.trim(), unit: editCat.unit });
      toast.success(data.name !== editCat.orig ? `Renamed to "${data.name}" — the items inside moved along` : "Category updated");
      setCustomCats((cs) => cs.map((c) => (c.name === editCat.orig ? data : c)));
      if (data.name !== editCat.orig) {
        setItems((list) => list.map((i) => (i.category === editCat.orig ? { ...i, category: data.name } : i)));
        setCatOrder((o) => o.map((n) => (n === editCat.orig ? data.name : n)));
        setOpenCats((prev) => { const s = new Set(prev); if (s.delete(editCat.orig)) s.add(data.name); return s; });
      }
      setEditCat(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCatEditBusy(false);
    }
  };

  const deleteCat = async (name) => {
    if (!window.confirm(`Delete the "${name}" category? Only possible while it has no items.`)) return;
    try {
      await api.delete(`/admin/library/categories/${encodeURIComponent(name)}`);
      toast.success(`"${name}" category deleted`);
      setCustomCats((cs) => cs.filter((c) => c.name !== name));
      setCatOrder((o) => o.filter((n) => n !== name));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const [costsOpen, setCostsOpen] = useState(false);  // always starts folded (owner preference)
  const toggleCosts = () => { setCostsOpen((o) => !o); };
  const debounce = useRef(null);
  const savedTimer = useRef(null);

  const load = useCallback((q = "") => {
    api.get("/admin/library", { params: q ? { search: q } : {} }).then(({ data }) => setItems(data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    api.get("/admin/library/categories").then(({ data }) => { setCustomCats(data.custom || []); setCatOrder(data.order || []); }).catch(() => {});
  }, [load]);

  const onSearch = (val) => {
    setSearch(val);
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => load(val), 250);
  };

  const toggleCat = (c) => setOpenCats((prev) => { const n = new Set(prev); if (n.has(c)) n.delete(c); else n.add(c); return n; });

  const flashSaved = () => {
    setSaveState("saved");
    clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setSaveState(null), 2000);
  };

  const replaceItem = (updated) => setItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));

  const patchItem = async (itemId, payload) => {
    setSaveState("saving");
    try {
      const { data } = await api.patch(`/admin/library/${itemId}`, payload);
      replaceItem(data);
      flashSaved();
      return data;
    } catch (err) {
      setSaveState(null);
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      return null;
    }
  };

  const openItem = (item) => {
    if (openItemId === item.id) {
      setOpenItemId(null);
      setDraft(null);
      setOpenSubId(null);
      setSitePickerSubId(null);
      return;
    }
    setOpenItemId(item.id);
    setDraft({
      id: item.id,
      name: item.name,
      category: item.category,
      unit: item.unit,
      subcategories: (item.subcategories || []).map((s) => ({ id: s.id || genId(), name: s.name, labor_rate: String(s.labor_rate ?? 0), material_rate: String(s.material_rate ?? 0), description: s.description || "", photo: s.photo || "" })),
    });
    setOpenSubId(null);
    setSitePickerSubId(null);
  };

  const createItem = async () => {
    if (!newForm.name.trim()) return toast.error("Item name is required");
    setCreating(true);
    try {
      const { data } = await api.post("/admin/library", {
        name: newForm.name, category: newForm.category, description: "",
        labor_rate: 0, material_rate: 0, subcategories: [],
        unit: newForm.unit || unitFor(newForm.category),
      });
      toast.success("Saved to library — now add its options below");
      setNewForm({ name: "", category: "Siding", unit: "" });
      setShowNew(false);
      setSearch("");
      await api.get("/admin/library").then(({ data: list }) => setItems(list));
      setOpenCats((prev) => new Set(prev).add(data.category));
      openItem(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const deactivate = async (item) => {
    if (!window.confirm(`Remove "${item.name}" from the library? Existing proposals keep their prices.`)) return;
    try {
      await api.delete(`/admin/library/${item.id}`);
      toast.success("Removed from library");
      if (openItemId === item.id) { setOpenItemId(null); setDraft(null); }
      load(search);
    } catch {
      toast.error("Could not remove");
    }
  };

  const createCost = async () => {
    if (!costForm.name.trim()) return toast.error("Cost name is required");
    try {
      await api.post("/admin/library", {
        name: costForm.name, category: "My Costs", description: "",
        calc_method: costForm.calc_method, cost_rate: parseFloat(costForm.cost_rate) || 0,
      });
      toast.success("Cost item saved — it auto-fills on every proposal's My Costs panel");
      setCostForm({ name: "", calc_method: "day", cost_rate: "" });
      setShowCostNew(false);
      load(search);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  // ---- Type-level auto-saves ----
  const saveTypeName = async () => {
    if (!draft) return;
    const item = items.find((i) => i.id === draft.id);
    const trimmed = draft.name.trim();
    if (!trimmed) {
      setDraft((d) => ({ ...d, name: item?.name || "" }));
      return toast.error("Name cannot be empty");
    }
    if (item && item.name === trimmed) return;
    await patchItem(draft.id, { name: trimmed });
  };

  const saveTypeCategory = async (category) => {
    if (!draft) return;
    setDraft((d) => ({ ...d, category, unit: unitFor(category) }));
    const updated = await patchItem(draft.id, { category });
    if (updated) {
      setOpenCats((prev) => new Set(prev).add(category)); // keep the destination group open
      load(search); // re-group under the new category
    }
  };

  const saveTypeUnit = async (unit) => {
    if (!draft) return;
    setDraft((d) => ({ ...d, unit }));
    await patchItem(draft.id, { unit });
  };

  // ---- Subcategory auto-saves ----
  const persistSubs = async (subs) => {
    if (!draft) return;
    const clean = subs
      .filter((s) => s.name.trim())
      .map((s) => ({
        id: s.id, name: s.name.trim(),
        labor_rate: parseFloat(s.labor_rate) || 0,
        material_rate: parseFloat(s.material_rate) || 0,
        description: (s.description || "").trim(),
        photo: s.photo || "",
      }));
    await patchItem(draft.id, { subcategories: clean });
  };

  const setSub = (subId, field, value) => {
    setDraft((d) => ({ ...d, subcategories: d.subcategories.map((s) => (s.id === subId ? { ...s, [field]: value } : s)) }));
  };

  const saveSubField = async (subId, field) => {
    if (!draft) return;
    const sub = draft.subcategories.find((s) => s.id === subId);
    if (!sub) return;
    if (field === "name" && !sub.name.trim()) {
      // revert empty name to the last saved one (or leave unsaved if brand new)
      const item = items.find((i) => i.id === draft.id);
      const orig = item?.subcategories?.find((s) => s.id === subId);
      if (orig) setSub(subId, "name", orig.name);
      return;
    }
    if (!sub.name.trim()) return; // brand-new sub without a name — nothing to persist yet
    await persistSubs(draft.subcategories);
  };

  const addSub = () => {
    const sub = blankSub();
    setDraft((d) => ({ ...d, subcategories: [...d.subcategories, sub] }));
    setOpenSubId(sub.id);
    setSitePickerSubId(null);
  };

  const removeSub = async (subId) => {
    const next = draft.subcategories.filter((s) => s.id !== subId);
    setDraft((d) => ({ ...d, subcategories: next }));
    if (openSubId === subId) setOpenSubId(null);
    if (sitePickerSubId === subId) setSitePickerSubId(null);
    await persistSubs(next);
  };

  const uploadSubPhoto = async (subId, file) => {
    setBusySubId(subId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const next = draft.subcategories.map((s) => (s.id === subId ? { ...s, photo: data.path } : s));
      setDraft((d) => ({ ...d, subcategories: next }));
      await persistSubs(next);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusySubId(null);
    }
  };

  const openSitePicker = async (subId) => {
    if (sitePickerSubId === subId) return setSitePickerSubId(null);
    setSitePickerSubId(subId);
    if (sitePhotos.length === 0) {
      try {
        const { data } = await api.get("/admin/photos");
        setSitePhotos(data);
      } catch {
        toast.error("Could not load website photos");
      }
    }
  };

  const attachSitePhoto = async (path) => {
    const subId = sitePickerSubId;
    if (!subId) return;
    const next = draft.subcategories.map((s) => (s.id === subId ? { ...s, photo: path } : s));
    setDraft((d) => ({ ...d, subcategories: next }));
    setSitePickerSubId(null);
    await persistSubs(next);
  };

  const removeSubPhoto = async (subId) => {
    const next = draft.subcategories.map((s) => (s.id === subId ? { ...s, photo: "" } : s));
    setDraft((d) => ({ ...d, subcategories: next }));
    await persistSubs(next);
  };

  const grouped = orderedCats.map((c) => ({ category: c, items: items.filter((i) => i.category === c) })).filter((g) => g.items.length || customUnits[g.category]);

  return (
    <div data-testid="admin-library-tab" className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-display text-4xl tracking-wide">LIBRARY</h1>
        <div className="flex items-center gap-2">
          <button onClick={() => { setShowNewCat(!showNewCat); setShowNew(false); }} data-testid="library-new-category-toggle" className={`flex items-center gap-2 border px-4 py-3 text-xs font-bold uppercase tracking-widest transition-colors duration-300 ${showNewCat ? "border-primary text-primary" : "border-white/20 text-white/60 hover:border-primary hover:text-primary"}`}>
            <Plus size={15} aria-hidden="true" /> Add Category
          </button>
          <button onClick={() => { setShowNew(!showNew); setShowNewCat(false); }} data-testid="library-new-toggle" className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-3 text-xs font-bold uppercase tracking-widest transition-transform duration-300 hover:-translate-y-0.5">
            <Plus size={15} aria-hidden="true" /> Add Item
          </button>
        </div>
      </div>
      <input value={search} onChange={(e) => onSearch(e.target.value)} placeholder="Search library — e.g. 'PVC garage'" data-testid="library-search" className={inputCls} />

      <div className="border border-primary/25 rounded-xl overflow-hidden" data-testid="library-costs-section">
        <button onClick={toggleCosts} data-testid="library-costs-toggle" aria-expanded={costsOpen} className={`w-full px-4 py-4 flex items-center justify-between gap-2 transition-colors duration-200 ${costsOpen ? "bg-primary/10" : "bg-primary/5 hover:bg-primary/10"}`}>
          <span className="font-display text-xl tracking-wider text-primary uppercase">My Costs</span>
          <span className="flex items-center gap-2 text-white/40 text-[11px] font-semibold">
            private — never shown to clients
            <ChevronDown size={14} className={`transition-transform duration-300 ${costsOpen ? "rotate-180 text-primary" : ""}`} aria-hidden="true" />
          </span>
        </button>
        {costsOpen && <div className="p-3 space-y-2">
          <p className="text-[11px] text-white/40">Your internal rates. On each proposal you enter days, workers and daily miles — these rates auto-calculate your estimated cost and expected profit.</p>
          {items.filter((i) => i.category === "My Costs").map((item) => (
            <CostRow key={item.id} item={item} onSave={patchItem} onDelete={deactivate} />
          ))}
          {showCostNew ? (
            <div className="border border-dashed border-primary/40 rounded-lg p-3 space-y-2" data-testid="cost-new-form">
              <input value={costForm.name} onChange={(e) => setCostForm({ ...costForm, name: e.target.value })} placeholder="Cost name — e.g. 'Equipment rental'" data-testid="cost-new-name" className={inputCls} />
              <div className="grid grid-cols-2 gap-2">
                <select value={costForm.calc_method} onChange={(e) => setCostForm({ ...costForm, calc_method: e.target.value })} aria-label="Calculation method" data-testid="cost-new-method" className={selectCls}>
                  {Object.entries(COST_METHOD_LABELS).map(([k, l]) => <option key={k} value={k} className="bg-[#252720]">{l}</option>)}
                </select>
                <input value={costForm.cost_rate} onChange={(e) => setCostForm({ ...costForm, cost_rate: e.target.value })} type="number" min="0" step="0.01" placeholder="$ rate" data-testid="cost-new-rate" className={inputCls} />
              </div>
              <div className="flex gap-2">
                <button onClick={createCost} data-testid="cost-new-save" className="flex-1 bg-primary text-primary-foreground font-bold uppercase tracking-widest text-xs py-3">Add cost item</button>
                <button onClick={() => setShowCostNew(false)} data-testid="cost-new-cancel" className="px-4 border border-white/20 text-white/60 text-xs font-bold uppercase tracking-widest rounded-lg">Cancel</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowCostNew(true)} data-testid="cost-new-toggle" className="flex items-center gap-2 border border-dashed border-white/25 rounded-lg px-3 py-2.5 text-[11px] font-bold uppercase tracking-widest text-white/60 hover:border-primary hover:text-primary transition-colors duration-300 w-full justify-center">
              <Plus size={13} aria-hidden="true" /> Add a cost item
            </button>
          )}
        </div>}
      </div>

      {showNewCat && (
        <div className="border border-primary/40 bg-primary/5 p-5 space-y-3" data-testid="library-new-category-form">
          <p className="text-xs font-bold uppercase tracking-widest text-primary">New Work Category — e.g. Framing, Decking, Gutters</p>
          <input value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} placeholder="Category name — e.g. 'Framing'" data-testid="library-category-name" className={inputCls} />
          <select value={catForm.unit} onChange={(e) => setCatForm({ ...catForm, unit: e.target.value })} aria-label="Default price unit" data-testid="library-category-unit" className={selectCls}>
            {UNIT_OPTIONS.map(([v, l]) => <option key={v} value={v} className="bg-[#252720]">{l}</option>)}
          </select>
          <p className="text-white/40 text-xs">This unit is the default for items in the category — you can override it per item.</p>
          <button onClick={createCategory} disabled={catBusy} data-testid="library-category-save" className="w-full bg-primary text-primary-foreground font-bold uppercase tracking-widest text-sm py-3.5 disabled:opacity-50">
            {catBusy ? "Saving..." : "Create Category"}
          </button>
        </div>
      )}

      {showNew && (
        <div className="border border-primary/40 bg-primary/5 p-5 space-y-3" data-testid="library-new-form">
          <p className="text-xs font-bold uppercase tracking-widest text-primary">New Library Item</p>
          <input value={newForm.name} onChange={(e) => setNewForm({ ...newForm, name: e.target.value })} placeholder="Name — e.g. 'Fiber Cement'" data-testid="library-new-name" className={inputCls} />
          <select value={newForm.category} onChange={(e) => setNewForm({ ...newForm, category: e.target.value, unit: "" })} data-testid="library-new-category" className={selectCls}>
            {allCats.map((c) => <option key={c} value={c} className="bg-[#252720]">{c}{customUnits[c] ? " · custom" : ""}</option>)}
          </select>
          <select value={newForm.unit || unitFor(newForm.category)} onChange={(e) => setNewForm({ ...newForm, unit: e.target.value })} aria-label="Calculate by" data-testid="library-new-unit" className={selectCls}>
            {UNIT_OPTIONS.map(([v, l]) => <option key={v} value={v} className="bg-[#252720]">{l}</option>)}
          </select>
          <p className="text-white/40 text-xs">Calculate by: <span className="text-primary font-bold">{unitLabel(newForm.unit || unitFor(newForm.category))}</span> — auto-set from the category, change it if you price this item differently. After saving, the item opens so you can add options — each with its own details, labor + material price and photo.</p>
          <button onClick={createItem} disabled={creating} data-testid="library-new-save" className="w-full bg-primary text-primary-foreground font-bold uppercase tracking-widest text-sm py-3.5 disabled:opacity-50">
            {creating ? "Saving..." : "Save to Library"}
          </button>
        </div>
      )}

      {grouped.map((g) => {
        const catOpen = !!search || openCats.has(g.category);
        return (
        <div key={g.category}
             onDragOver={(e) => e.preventDefault()}
             onDrop={() => dropOn(g.category)}
             className={`border rounded-xl overflow-hidden mt-4 transition-colors duration-200 ${dragCat === g.category ? "border-primary" : "border-white/10"}`}
             data-testid={`lib-group-${g.category}`}>
          <button onClick={() => toggleCat(g.category)} data-testid={`lib-cat-${g.category}`} className={`w-full px-4 py-4 flex items-center justify-between transition-colors duration-200 ${catOpen ? "bg-primary/10" : "bg-white/[0.06] hover:bg-white/10"}`}>
            <span className="flex items-center gap-2 min-w-0">
              <span draggable
                    onDragStart={(e) => { setDragCat(g.category); e.dataTransfer.effectAllowed = "move"; }}
                    onDragEnd={() => setDragCat(null)}
                    onClick={(e) => e.stopPropagation()}
                    title="Drag to reorder — your best sellers on top"
                    data-testid={`lib-cat-drag-${g.category}`}
                    className="cursor-grab active:cursor-grabbing text-white/25 hover:text-primary transition-colors duration-200 shrink-0 py-1">
                <GripVertical size={15} aria-hidden="true" />
              </span>
              <span className="font-display text-xl tracking-wider text-primary uppercase truncate">{g.category}</span>
            </span>
            <span className="flex items-center gap-1.5 text-white/40 text-[11px] font-semibold">
              {customUnits[g.category] && (
                <>
                  <span role="button" aria-label={`Edit category ${g.category}`} data-testid={`lib-cat-edit-${g.category}`}
                        onClick={(e) => { e.stopPropagation(); setEditCat({ orig: g.category, name: g.category, unit: unitFor(g.category) }); }}
                        className="p-1 hover:text-primary transition-colors duration-200 cursor-pointer"><Pencil size={13} /></span>
                  <span role="button" aria-label={`Delete category ${g.category}`} data-testid={`lib-cat-delete-${g.category}`}
                        onClick={(e) => { e.stopPropagation(); deleteCat(g.category); }}
                        className="p-1 hover:text-red-300 transition-colors duration-200 cursor-pointer"><Trash2 size={13} /></span>
                </>
              )}
              {g.items.length} types · {unitFor(g.category)} <ChevronDown size={14} className={`transition-transform duration-300 ${catOpen ? "rotate-180 text-primary" : ""}`} aria-hidden="true" />
            </span>
          </button>
          {editCat?.orig === g.category && (
            <div className="p-3 space-y-2 border-t border-primary/30 bg-primary/5" data-testid={`lib-cat-edit-form-${g.category}`}>
              <input value={editCat.name} onChange={(e) => setEditCat({ ...editCat, name: e.target.value })} placeholder="Category name" data-testid="lib-cat-edit-name" className={inputCls} />
              <select value={editCat.unit} onChange={(e) => setEditCat({ ...editCat, unit: e.target.value })} aria-label="Default price unit" data-testid="lib-cat-edit-unit" className={selectCls}>
                {UNIT_OPTIONS.map(([v, l]) => <option key={v} value={v} className="bg-[#252720]">{l}</option>)}
              </select>
              <div className="flex gap-2">
                <button onClick={saveCatEdit} disabled={catEditBusy} data-testid="lib-cat-edit-save" className="flex-1 bg-primary text-primary-foreground font-bold uppercase tracking-widest text-[11px] py-2.5 rounded-lg disabled:opacity-50">{catEditBusy ? "Saving..." : "Save category"}</button>
                <button onClick={() => setEditCat(null)} data-testid="lib-cat-edit-cancel" className="px-4 border border-white/20 text-white/60 text-[11px] font-bold uppercase tracking-widest rounded-lg">Cancel</button>
              </div>
            </div>
          )}
          {catOpen && <div className="space-y-2 p-3">
            {g.items.map((item) => (
              <div key={item.id} className="border border-white/10 rounded-xl bg-card shadow-[0_4px_20px_rgba(0,0,0,0.3)] overflow-hidden" data-testid={`library-item-${item.id}`}>
                <div className="p-3.5 flex items-center gap-3">
                  <button onClick={() => openItem(item)} data-testid={`lib-type-${item.id}`} className="flex-1 min-w-0 text-left flex items-center gap-2">
                    <span className="flex-1 min-w-0">
                      <span className="block font-semibold text-sm truncate">{item.name}{item.is_custom ? <span className="text-white/35 font-normal"> · custom</span> : null}</span>
                      <span className="block text-[11px] text-white/45">{item.subcategories?.length > 0 ? `${item.subcategories.length} option${item.subcategories.length === 1 ? "" : "s"} — tap to edit` : "no options yet — tap to add"} · per {unitLabel(item.unit)}</span>
                    </span>
                    <ChevronDown size={15} className={`shrink-0 text-white/40 transition-transform duration-300 ${openItemId === item.id ? "rotate-180 text-primary" : ""}`} aria-hidden="true" />
                  </button>
                  <button onClick={() => deactivate(item)} data-testid={`library-delete-${item.id}`} aria-label="Remove" className="text-red-400/70 hover:text-red-300 p-2"><Trash2 size={15} /></button>
                </div>

                {openItemId === item.id && draft && (
                  <div className="border-t border-white/10 px-3.5 py-4 space-y-4 bg-white/[0.02]" data-testid={`lib-editor-${item.id}`}>
                    <div className="flex items-center justify-between">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-white/40">Type settings — changes save automatically</p>
                      <span className={`text-[10px] font-bold uppercase tracking-widest transition-opacity duration-300 ${saveState ? "opacity-100" : "opacity-0"} ${saveState === "saving" ? "text-white/45" : "text-primary"}`} data-testid={`lib-save-state-${item.id}`}>
                        {saveState === "saving" ? "Saving..." : "Saved"}
                      </span>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-2">
                      <input value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} onBlur={saveTypeName} placeholder="Type name — e.g. 'Fiber Cement'" aria-label="Type name" data-testid={`lib-type-name-${item.id}`} className={inputCls} />
                      <select value={draft.category} onChange={(e) => saveTypeCategory(e.target.value)} aria-label="Type category" data-testid={`lib-type-category-${item.id}`} className={selectCls}>
                        {allCats.map((c) => <option key={c} value={c} className="bg-[#252720]">{c}</option>)}
                      </select>
                    </div>
                    <select value={draft.unit} onChange={(e) => saveTypeUnit(e.target.value)} aria-label="Price unit" data-testid={`lib-type-unit-${item.id}`} className={selectCls}>
                      {UNIT_OPTIONS.map(([v, l]) => <option key={v} value={v} className="bg-[#252720]">{l}</option>)}
                    </select>

                    <div className="space-y-2">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-white/40">Options / Subcategories — tap one to edit its details, prices and photo</p>
                      {draft.subcategories.length === 0 && <p className="text-[11px] text-white/40">No options yet — add sizes, colors or styles below.</p>}
                      {draft.subcategories.map((s) => {
                        const subOpen = openSubId === s.id;
                        const labor = parseFloat(s.labor_rate) || 0;
                        const material = parseFloat(s.material_rate) || 0;
                        return (
                          <div key={s.id} className={`border rounded-lg overflow-hidden ${subOpen ? "border-primary/50" : "border-white/10"}`} data-testid={`lib-sub-${s.id}`}>
                            <div className="flex items-center gap-2 px-3 py-2.5 bg-white/5">
                              {s.photo && <img src={mediaUrl(s.photo)} alt={s.name} className="w-9 h-9 object-cover border border-white/15 shrink-0" loading="lazy" data-testid={`lib-sub-thumb-${s.id}`} />}
                              <button onClick={() => { setOpenSubId(subOpen ? null : s.id); setSitePickerSubId(null); }} data-testid={`lib-sub-toggle-${s.id}`} className="flex-1 min-w-0 text-left flex items-center gap-2">
                                <span className="flex-1 min-w-0">
                                  <span className="block font-semibold text-xs text-white/85 truncate">{s.name || "New option — tap to name it"}</span>
                                  <span className="block text-[10px] text-white/45">labor ${labor.toFixed(2)} + material ${material.toFixed(2)} = ${(labor + material).toFixed(2)}/{draft.unit}</span>
                                </span>
                                <ChevronDown size={14} className={`shrink-0 text-white/40 transition-transform duration-300 ${subOpen ? "rotate-180 text-primary" : ""}`} aria-hidden="true" />
                              </button>
                              <button onClick={() => removeSub(s.id)} aria-label={`Remove ${s.name || "option"}`} data-testid={`lib-sub-remove-${s.id}`} className="text-red-400/70 hover:text-red-300 p-1.5"><X size={14} /></button>
                            </div>

                            {subOpen && (
                              <div className="p-3 space-y-2.5 border-t border-white/10" data-testid={`lib-sub-editor-${s.id}`}>
                                <input value={s.name} onChange={(e) => setSub(s.id, "name", e.target.value)} onBlur={() => saveSubField(s.id, "name")} placeholder="Option name — e.g. 'Double 4 in' or 'Charcoal'" aria-label="Option name" data-testid={`lib-sub-name-${s.id}`} className={inputCls} />
                                <input value={s.description} onChange={(e) => setSub(s.id, "description", e.target.value)} onBlur={() => saveSubField(s.id, "description")} placeholder="Special details for clients (optional) — shown on the proposal" aria-label="Special details" data-testid={`lib-sub-details-${s.id}`} className={inputCls} />
                                <div className="grid grid-cols-2 gap-2">
                                  <input value={s.labor_rate} onChange={(e) => setSub(s.id, "labor_rate", e.target.value)} onBlur={() => saveSubField(s.id, "labor_rate")} type="number" min="0" step="0.01" placeholder={`Labor $/${draft.unit}`} aria-label="Labor price" data-testid={`lib-sub-labor-${s.id}`} className={inputCls} />
                                  <input value={s.material_rate} onChange={(e) => setSub(s.id, "material_rate", e.target.value)} onBlur={() => saveSubField(s.id, "material_rate")} type="number" min="0" step="0.01" placeholder={`Material $/${draft.unit}`} aria-label="Material price" data-testid={`lib-sub-material-${s.id}`} className={inputCls} />
                                </div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  {s.photo && <img src={mediaUrl(s.photo)} alt={s.name} className="w-12 h-12 object-cover border border-white/15" data-testid={`lib-sub-photo-preview-${s.id}`} />}
                                  <label className="flex items-center gap-1.5 border border-dashed border-white/20 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-widest text-white/55 hover:border-primary hover:text-primary transition-colors duration-300 cursor-pointer">
                                    <Images size={12} aria-hidden="true" /> {busySubId === s.id ? "Uploading..." : s.photo ? "Change photo" : "Photo from phone"}
                                    <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadSubPhoto(s.id, f); e.target.value = ""; }} data-testid={`lib-sub-photo-input-${s.id}`} />
                                  </label>
                                  <button onClick={() => openSitePicker(s.id)} data-testid={`lib-sub-site-${s.id}`} className={`flex items-center gap-1.5 border px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-widest transition-colors duration-300 ${sitePickerSubId === s.id ? "border-primary text-primary" : "border-dashed border-white/20 text-white/55 hover:border-primary hover:text-primary"}`}>
                                    <FolderOpen size={12} aria-hidden="true" /> From website
                                  </button>
                                  {s.photo && (
                                    <button onClick={() => removeSubPhoto(s.id)} data-testid={`lib-sub-photo-remove-${s.id}`} className="text-[10px] font-bold uppercase tracking-widest text-red-400/70 hover:text-red-300 px-1.5 py-1.5">
                                      Remove photo
                                    </button>
                                  )}
                                </div>
                                {sitePickerSubId === s.id && <SitePhotoPicker photos={sitePhotos} onPick={attachSitePhoto} idPrefix={`lib-sub-${s.id}`} />}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      <button onClick={addSub} data-testid={`lib-sub-add-${item.id}`} className="flex items-center gap-2 border border-dashed border-white/25 px-3 py-2.5 text-[11px] font-bold uppercase tracking-widest text-white/60 hover:border-primary hover:text-primary transition-colors duration-300 w-full justify-center">
                        <Plus size={13} aria-hidden="true" /> Add another subcategory
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>}
        </div>
        );
      })}
      {items.length === 0 && <p className="text-white/40 text-sm py-10 text-center" data-testid="library-empty">No items match your search.</p>}
    </div>
  );
};
